import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { 
  Home, 
  Receipt, 
  Target, 
  BarChart3, 
  Users,
  Wallet,
  TrendingUp,
  Bell,
  Hand,
  Landmark,
  FileClock,
  User, 
  LogOut, 
  Moon, 
  Sun,
  Menu,
  X,
  Archive,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'

const Navbar = ({ isDark, toggleDarkMode, isSidebarExpanded, setIsSidebarExpanded }) => {
  const { user, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  const handleMobileNavClick = () => {
    setIsMobileSidebarOpen(false)
  }

  const navItems = [
    { to: '/dashboard', icon: Home, label: 'Dashboard' },
    { to: '/expenses', icon: Receipt, label: 'Expenses' },
    { to: '/budgets', icon: Target, label: 'Budgets' },
    { to: '/accounts', icon: Wallet, label: 'Accounts' },
    { to: '/incomes', icon: TrendingUp, label: 'Income' },
    { to: '/bills', icon: FileClock, label: 'Bills' },
    { to: '/goals', icon: Landmark, label: 'Goals' },
    { to: '/debts', icon: Hand, label: 'Debts' },
    { to: '/insights', icon: BarChart3, label: 'Insights' },
    { to: '/notifications', icon: Bell, label: 'Alerts' },
    { to: '/collaboration', icon: Users, label: 'Team' },
    { to: '/budget-dashboard', icon: Users, label: 'Shared Budgets' },
    { to: '/stats', icon: BarChart3, label: 'Statistics' },
    { to: '/archives', icon: Archive, label: 'Archives' },
    { to: '/profile', icon: User, label: 'Profile' },
  ]

  const isActive = (path) => location.pathname === path

  if (!user) {
    return null
  }

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 h-16 border-b border-gray-200 bg-white/95 shadow-md backdrop-blur dark:border-gray-700 dark:bg-gray-800/95">
        <div className="flex h-full items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMobileSidebarOpen((prev) => !prev)}
              className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 lg:hidden"
              aria-label="Toggle sidebar"
            >
              {isMobileSidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

            <button
              onClick={() => setIsSidebarExpanded((prev) => !prev)}
              className="hidden rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 lg:inline-flex"
              aria-label={isSidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {isSidebarExpanded ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </button>

            <Link to="/dashboard" className="flex items-center gap-2">
              <Receipt className="h-8 w-8 text-primary-600" />
              <span className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">ExpenseTracker</span>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleDarkMode}
              className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              aria-label="Toggle dark mode"
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            <div className="hidden items-center gap-2 rounded-lg px-3 py-2 text-sm sm:flex">
              <User className="h-4 w-4 text-gray-600 dark:text-gray-300" />
              <span className="max-w-[200px] truncate text-gray-700 dark:text-gray-300">{user.email}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside
        className={`fixed left-0 top-16 z-40 hidden h-[calc(100vh-4rem)] flex-col border-r border-gray-200 bg-white/95 shadow-lg backdrop-blur transition-all duration-300 dark:border-gray-700 dark:bg-gray-800/95 lg:flex ${
          isSidebarExpanded ? 'w-72' : 'w-20'
        }`}
      >
        <div className="flex-1 overflow-y-auto p-3">
          {isSidebarExpanded && (
            <p className="mb-3 px-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
              Navigation
            </p>
          )}

          <nav className="flex flex-col gap-1">
            {navItems.map((item) => {
              const Icon = item.icon
              const active = isActive(item.to)

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  title={item.label}
                  className={`group flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all ${
                    isSidebarExpanded ? 'justify-start gap-3' : 'justify-center'
                  } ${
                    active
                      ? 'bg-primary-100 text-primary-700 shadow-sm dark:bg-primary-900/50 dark:text-primary-300'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {isSidebarExpanded && <span className="truncate">{item.label}</span>}
                </Link>
              )
            })}
          </nav>
        </div>

        <div className="border-t border-gray-200 p-3 dark:border-gray-700">
          {isSidebarExpanded ? (
            <>
              <p className="truncate text-sm text-gray-600 dark:text-gray-300">{user.email}</p>
              <button
                onClick={handleSignOut}
                className="mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <LogOut className="h-4 w-4" />
                <span>Sign Out</span>
              </button>
            </>
          ) : (
            <button
              onClick={handleSignOut}
              title="Sign Out"
              className="flex w-full items-center justify-center rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobileSidebarOpen && (
        <button
          type="button"
          aria-label="Close sidebar overlay"
          onClick={() => setIsMobileSidebarOpen(false)}
          className="fixed inset-0 top-16 z-40 bg-black/40 lg:hidden"
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`fixed left-0 top-16 z-50 h-[calc(100vh-4rem)] w-72 border-r border-gray-200 bg-white shadow-xl transition-transform duration-300 dark:border-gray-700 dark:bg-gray-800 lg:hidden ${
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col">
          <div className="flex-1 overflow-y-auto p-3">
            <p className="mb-3 px-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
              Navigation
            </p>

            <nav className="flex flex-col gap-1">
              {navItems.map((item) => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={handleMobileNavClick}
                    className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive(item.to)
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </Link>
                )
              })}
            </nav>
          </div>

          <div className="border-t border-gray-200 p-3 dark:border-gray-700">
            <p className="truncate text-sm text-gray-600 dark:text-gray-300">{user.email}</p>
            <button
              onClick={handleSignOut}
              className="mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}

export default Navbar
